import React, { useEffect, useRef, useState } from "react";
import { getTokens } from "./auth/keycloakClient";
import logo2 from "./logo2.svg";
import TestcaseParser from "./testcaseParser";
import ExpandButton from "./components/ExpandButton";
import CopyButton from "./components/CopyButton";
import { InfoIcon, ChipIcon } from "./components/Icons";
import CompartmentList from "./components/CompartmentList";
import ForceLineChart from "./components/ForceLineChart";
import { FaRegFileCode } from "react-icons/fa";



import { EXECUTE_URL, TESTCASE_QUERY, REVIEWS_QUERY, TEXTAREA_CONFIG } from "./config/constants";


export default function App() {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Separate states for structured output
  const [analysis, setAnalysis] = useState("");
  const [testCasesText, setTestCasesText] = useState("");
  const [robotFrameworkCode, setRobotFrameworkCode] = useState("");
  const [savedRobotFrameworkCode, setSavedRobotFrameworkCode] = useState("");
  const [showRobotFramework, setShowRobotFramework] = useState(false);
  const [isPrintingRobot, setIsPrintingRobot] = useState(false);
  const [isGeneratingRobot, setIsGeneratingRobot] = useState(false);

  // Dynamic testcases and expansion states
  const [dynamicTestcases, setDynamicTestcases] = useState([]);
  const [expandedTestcases, setExpandedTestcases] = useState({});

  // Dynamic Robot Framework tests and expansion states
  const [dynamicRobotTests, setDynamicRobotTests] = useState([]);
  const [expandedRobotTests, setExpandedRobotTests] = useState({});

  // Click expansion state
  const [expandedPanel, setExpandedPanel] = useState(null);

  // Chart data state
  const [chartData, setChartData] = useState(null);

  // Streaming toggle
  const [isStreamingEnabled, setIsStreamingEnabled] = useState(true);

  const currentSectionRef = useRef("analysis");
  const textBufferRef = useRef("");
  const chartDataSetRef = useRef(false);

  const textareaRef = useRef(null);
  const testcaseParser = useRef(null);
  const abortRef = useRef(null);
  const scrollRefAnalysis = useRef(null);
  const scrollRefTestCases = useRef(null);
  const scrollRefCode = useRef(null);

  // Initialize Parsers
  useEffect(() => {
    testcaseParser.current = new TestcaseParser();
  }, []);

  // Auto-resize textarea function
  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, TEXTAREA_CONFIG.MAX_HEIGHT);
      textarea.style.height = newHeight + 'px';

      // Show scrollbar only when content exceeds max height
      textarea.style.overflowY = textarea.scrollHeight > TEXTAREA_CONFIG.MAX_HEIGHT ? 'auto' : 'hidden';
    }
  };

  // Handle question change with auto-resize
  const handleQuestionChange = (e) => {
    setQuestion(e.target.value);
    setTimeout(autoResizeTextarea, 0); // Run after state update
  };

  // Handle panel click to expand/collapse
  const handlePanelClick = (panelId) => {
    setExpandedPanel(expandedPanel === panelId ? null : panelId);
  };

  // Keep each panel scrolled to bottom while streaming
  useEffect(() => {
    if (scrollRefAnalysis.current) {
      scrollRefAnalysis.current.scrollTop = scrollRefAnalysis.current.scrollHeight;
    }
  }, [analysis, loading]);

  useEffect(() => {
    if (scrollRefTestCases.current) {
      scrollRefTestCases.current.scrollTop = scrollRefTestCases.current.scrollHeight;
    }
  }, [testCasesText, loading]);

  useEffect(() => {
    if (scrollRefCode.current) {
      scrollRefCode.current.scrollTop = scrollRefCode.current.scrollHeight;
    }
  }, [robotFrameworkCode, loading]);

  const handleStreamOutput = (outputText, isNewMsg = false) => {
    if (isNewMsg) {
      setAnalysis("");
      setTestCasesText("");
      setRobotFrameworkCode("");
      setDynamicTestcases([]);
      setDynamicRobotTests([]);
      setChartData(null);
      setIsGeneratingRobot(false);
      currentSectionRef.current = "analysis";
      textBufferRef.current = "";
      chartDataSetRef.current = false;

      // Reset parsers
      if (testcaseParser.current) {
        testcaseParser.current.resetState();
      }
      return;
    }

    if (!outputText) return;

    // Add new text to buffer
    textBufferRef.current += outputText;
    let bufferContent = textBufferRef.current;

    console.log('📥 handleStreamOutput called - current section:', currentSectionRef.current, 'buffer length:', bufferContent.length);

    // Define markers as single words
    const FORCECURVES_MARKER = "FORCECURVES";
    const TESTCASES_MARKER = "ENDOFTESTCASES";
    const JSONOUTPUT_MARKER = "ENDOFJSONOUTPUT";

    // Check for FORCECURVES marker (handles **FORCECURVES:** format)
    if (currentSectionRef.current === "analysis") {
      // Use regex to find FORCECURVES with optional ** and :
      const forceCurvesRegex = /\*{0,2}FORCECURVES\*{0,2}:?/i;
      const match = bufferContent.match(forceCurvesRegex);

      if (match) {
        const analysisIndex = match.index;
        const markerLength = match[0].length;

        // Get text before and after marker
        const beforeMarker = bufferContent.substring(0, analysisIndex).trim();
        const afterMarkerStart = analysisIndex + markerLength;
        const afterMarker = bufferContent.substring(afterMarkerStart).trim();
        
        // Add everything before marker to analysis
        if (beforeMarker) {
          setAnalysis((prev) => prev + beforeMarker);
        }

        // Switch to testcases section
        currentSectionRef.current = "testcases";
        
        // Set the buffer to the text after marker
        textBufferRef.current = afterMarker;
        
        // If there's text after marker, process it immediately
        if (afterMarker && afterMarker.length > 0) {
          setTimeout(() => {
            if (currentSectionRef.current === "testcases" && textBufferRef.current) {
              // Check if the afterMarker contains ENDOFTESTCASES or ENDOFJSONOUTPUT already
              const testcasesIndex = textBufferRef.current.indexOf(TESTCASES_MARKER);
              const jsonOutputIndex = textBufferRef.current.indexOf(JSONOUTPUT_MARKER);

              if (testcasesIndex === -1 && jsonOutputIndex === -1) {
                // No second marker yet, add all to testcases
                setTestCasesText((prev) => prev + textBufferRef.current);
                textBufferRef.current = "";
              } else {
                // Second marker found immediately, process it
                handleStreamOutput("", false);
              }
            }
          }, 10);
        }
        
        return;
      }
    }
    
    // Check for ENDOFTESTCASES or ENDOFJSONOUTPUT marker
    if (currentSectionRef.current === "testcases") {
      const testcasesIndex = bufferContent.indexOf(TESTCASES_MARKER);
      const jsonOutputIndex = bufferContent.indexOf(JSONOUTPUT_MARKER);

      console.log('🔎 Checking for markers in testcases section');
      console.log('   Buffer content length:', bufferContent.length);
      console.log('   Last 200 chars of buffer:', bufferContent.slice(-200));
      console.log('   ENDOFTESTCASES found at:', testcasesIndex);
      console.log('   ENDOFJSONOUTPUT found at:', jsonOutputIndex);

      // Find which marker appears first (or if any appears at all)
      let markerIndex = -1;
      let markerLength = 0;

      if (testcasesIndex !== -1 && jsonOutputIndex !== -1) {
        // Both markers found, use the one that appears first
        if (testcasesIndex < jsonOutputIndex) {
          markerIndex = testcasesIndex;
          markerLength = TESTCASES_MARKER.length;
        } else {
          markerIndex = jsonOutputIndex;
          markerLength = JSONOUTPUT_MARKER.length;
        }
      } else if (testcasesIndex !== -1) {
        markerIndex = testcasesIndex;
        markerLength = TESTCASES_MARKER.length;
      } else if (jsonOutputIndex !== -1) {
        markerIndex = jsonOutputIndex;
        markerLength = JSONOUTPUT_MARKER.length;
      }

      if (markerIndex !== -1) {
        const usedMarker = (testcasesIndex !== -1 && markerIndex === testcasesIndex) ? 'ENDOFTESTCASES' : 'ENDOFJSONOUTPUT';
        console.log(`🎯 Found marker: ${usedMarker} at index ${markerIndex}`);

        // Get text before and after marker
        const beforeMarker = bufferContent.substring(0, markerIndex).trim();
        const afterMarkerStart = markerIndex + markerLength;
        const afterMarker = bufferContent.substring(afterMarkerStart).trim();
        console.log('📝 Text after marker (first 100 chars):', afterMarker.substring(0, 100));

        // Add everything before marker to testcases
        if (beforeMarker) {
          setTestCasesText((prev) => prev + beforeMarker);
        }

        // Process through parser for chart data (always, even if beforeMarker is empty)
        if (testcaseParser.current) {
          const response = testcaseParser.current.processStreamContent({
            content: beforeMarker,
            section: "testcases"
          });

          // Set chart data ONLY ONCE when parser first finds it (using ref to prevent race conditions)
          if (response.chartData && !chartDataSetRef.current) {
            console.log('📊 Chart data from parser! Series count:', response.chartData.series?.length || 0);
            setChartData(response.chartData);
            chartDataSetRef.current = true;
          }
        }

        // ENDOFJSONOUTPUT marker detected - chart data should be parsed now
        if (usedMarker === 'ENDOFJSONOUTPUT') {
          console.log('🎨 ENDOFJSONOUTPUT detected - chart should be rendered now');
        }

        // Switch to robot section
        currentSectionRef.current = "robot";
        setIsGeneratingRobot(true);

        // Set buffer to text after marker
        textBufferRef.current = afterMarker;

        // If there's text after marker, save it but don't display
        if (afterMarker && afterMarker.length > 0) {
          setTimeout(() => {
            if (currentSectionRef.current === "robot" && textBufferRef.current) {
              console.log('🔍 [setTimeout] Saving to savedRobotFrameworkCode:', textBufferRef.current);
              setSavedRobotFrameworkCode((prev) => prev + textBufferRef.current);
              textBufferRef.current = "";
            }
          }, 10);
        }

        return;
      }
    }
    
    // Check if we might be building a marker (partial detection)
    // With single-word markers, we need simpler partial detection
    const partialMarkers = [
      // FORCECURVES marker partials (including possible ** prefix)
      "*FORCECURVES", "**FORCECURVES", "*FORCECURVE", "**FORCECURVE",
      "FORCECURVE", "FORCECURV", "FORCECUR", "FORCECU", "FORCEC", "FORCE", "FORC", "FOR",
      "*FORCECURV", "**FORCECURV", "*FORCECUR", "**FORCECUR", "*FORCECU", "**FORCECU",
      "*FORCEC", "**FORCEC", "*FORCE", "**FORCE", "*FORC", "**FORC", "*FOR", "**FOR",
      // ENDOFTESTCASES marker partials
      "ENDOFTESTCASE", "ENDOFTESTCAS", "ENDOFTESTCA", "ENDOFTESTC",
      "ENDOFTEST", "ENDOFTES", "ENDOFTE", "ENDOFT",
      // ENDOFJSONOUTPUT marker partials
      "ENDOFJSONOUTPU", "ENDOFJSONOUTP", "ENDOFJSONOUT", "ENDOFJSONOU", "ENDOFJSONO",
      "ENDOFJSON", "ENDOFJSO", "ENDOFJS", "ENDOFJ",
      // Common short partials for both END* markers
      "ENDOF", "ENDO", "END", "EN"
    ];
    
    let mightBeMarker = false;
    for (const partial of partialMarkers) {
      if (bufferContent.endsWith(partial)) {
        mightBeMarker = true;
        break;
      }
    }
    
    if (!mightBeMarker) {
      // No marker or partial marker - flush buffer to current section
      if (currentSectionRef.current === "analysis") {
        if (isStreamingEnabled) {
          setAnalysis((prev) => prev + bufferContent);
        }
      } else if (currentSectionRef.current === "testcases") {
        if (isStreamingEnabled) {
          setTestCasesText((prev) => prev + bufferContent);

          // Process through parser for chart data only
          if (testcaseParser.current) {
            const response = testcaseParser.current.processStreamContent({
              content: bufferContent,
              section: "testcases"
            });

            // Set chart data ONLY ONCE when parser first finds it (using ref to prevent race conditions)
            if (response.chartData && !chartDataSetRef.current) {
              console.log('📊 Chart data from parser! Series count:', response.chartData.series?.length || 0);
              setChartData(response.chartData);
              chartDataSetRef.current = true;
            }
          }
        }
      } else if (currentSectionRef.current === "robot") {
        // Robot framework ALWAYS just saves content, NEVER displays live
        // No matter if chat or button - always collect, never stream
        console.log('🔍 [robot section] Saving to savedRobotFrameworkCode:', bufferContent);
        setSavedRobotFrameworkCode((prev) => prev + bufferContent);
      }
      // Only clear buffer when streaming is enabled - when OFF, keep accumulating for marker detection
      // Exception: always clear robot buffer since it's already saved
      if (isStreamingEnabled || currentSectionRef.current === "robot") {
        textBufferRef.current = "";
      }
    }
    // If mightBeMarker is true, keep text in buffer and wait for more
  };

  // Generic Stream-Handler function
  const handleStream = async (query) => {
    if (loading) return;

    // Immediate content refresh - clear everything to initial state
    setAnalysis("");
    setTestCasesText("");
    setRobotFrameworkCode("");
    setSavedRobotFrameworkCode("");
    setDynamicTestcases([]);
    setExpandedTestcases({});
    setDynamicRobotTests([]);
    setExpandedRobotTests({});
    setChartData(null);
    setShowRobotFramework(false);  // Always reset showRobotFramework
    setIsGeneratingRobot(false);
    setErr(null);
    setLoading(true);
    currentSectionRef.current = "analysis";
    textBufferRef.current = "";
    chartDataSetRef.current = false;

    // Reset parser
    if (testcaseParser.current) {
      testcaseParser.current.resetState();
    }

    try {
      console.log('🔑 DEBUG: Getting tokens...');
      const { access_token, refresh_token } = await getTokens();
      console.log('🔑 DEBUG: Got access token:', access_token ? `${access_token.substring(0, 50)}...` : 'MISSING');

      const body = {
        query: query,
        webhook_url: null,
        refresh_token: refresh_token || "",
        context_building_mode: "auto",
        execute_with_code_generation: false,
      };

      console.log('📤 DEBUG: Sending request to:', EXECUTE_URL);
      console.log('📤 DEBUG: Request body:', JSON.stringify(body, null, 2));

      const controller = new AbortController();
      abortRef.current = controller;

      const resp = await fetch(EXECUTE_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${access_token}`,
          "Content-Type": "application/json",
          Accept: "text/event-stream, application/x-ndjson, application/json",
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      console.log('📥 DEBUG: Response status:', resp.status, resp.statusText);
      console.log('📥 DEBUG: Response headers:', Object.fromEntries(resp.headers.entries()));

      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => "");
        console.error('❌ DEBUG: Response error:', text);
        throw new Error(`HTTP ${resp.status} ${resp.statusText}${text ? " - " + text : ""}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      const handleEventObject = (obj) => {
        console.log('🔍 DEBUG: Full API response object:', JSON.stringify(obj, null, 2));

        const diana = obj?.diana_agent_execution;
        if (diana) {
          console.log('🔍 DEBUG: diana_agent_execution output:', diana.output);
          const output = diana.output ?? "";
          handleStreamOutput(output, !!diana.is_beginning_of_new_message);
        }

        if (Array.isArray(obj?.execution_layers)) {
          for (const layer of obj.execution_layers) {
            const execs = layer?.agent_executions || [];
            for (const ex of execs) {
              if (typeof ex?.output === "string" && ex.output.length > 0) {
                console.log('🔍 DEBUG: execution_layers output:', ex.output);
                handleStreamOutput(ex.output, false);
              }
              if (ex?.execution_status === "FAILED" && ex?.error_message) {
                console.error('❌ DEBUG: agent execution failed:', ex.error_message);
                setErr(`Agent execution failed on the backend: ${ex.error_message}`);
              }
            }
          }
        }
      };

      const processNdjsonLine = (line) => {
        const s = line.trim();
        if (!s || s === "[DONE]") return;
        try {
          handleEventObject(JSON.parse(s));
        } catch {
          /* ignore */
        }
      };

      const processSseBlock = (block) => {
        const lines = block.split(/\r?\n/);
        const dataLines = [];
        for (const l of lines) if (l.startsWith("data:")) dataLines.push(l.slice(5).trim());
        if (!dataLines.length) return false;
        const payload = dataLines.join("\n").trim();
        if (!payload || payload === "[DONE]") return true;
        try {
          handleEventObject(JSON.parse(payload));
          return true;
        } catch {
          return false;
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let sseParts = buffer.split(/\r?\n\r?\n/);
        buffer = sseParts.pop() ?? "";
        for (const blk of sseParts) {
          const handled = processSseBlock(blk);
          if (!handled) blk.split(/\r?\n/).forEach(processNdjsonLine);
        }

        const ndjsonParts = buffer.split(/\r?\n/);
        buffer = ndjsonParts.pop() ?? "";
        ndjsonParts.forEach(processNdjsonLine);
      }

      if (buffer.trim()) {
        if (!processSseBlock(buffer)) processNdjsonLine(buffer);
      }
      
      // Final cleanup - flush any remaining buffer content
      if (textBufferRef.current) {
        const remaining = textBufferRef.current;
        if (currentSectionRef.current === "analysis") {
          setAnalysis((prev) => prev + remaining);
        } else if (currentSectionRef.current === "testcases") {
          setTestCasesText((prev) => prev + remaining);
        } else if (currentSectionRef.current === "robot") {
          // Just save the content, NEVER auto-display compartments from chat
          setSavedRobotFrameworkCode((prev) => prev + remaining);
        }
        textBufferRef.current = "";
      }
      
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error(e);
        setErr(e.message || "Streaming failed. Check Keycloak token / execute URL.");
      }
    } finally {
      setLoading(false);
      setIsGeneratingRobot(false);
      abortRef.current = null;
    }
  };

  const ask = async () => {
    const q = question.trim();
    if (!q || loading) return;
    await handleStream(q);
  };

  const generateTestcases = () => {
    handleStream(TESTCASE_QUERY);
  };

  const generateReviews = () => {
    handleStream(REVIEWS_QUERY);
  };

  const generateRobotFrameworkTests = () => {
    console.log('🔘 Button clicked! savedRobotFrameworkCode length:', savedRobotFrameworkCode?.length || 0);
    console.log('🔘 savedRobotFrameworkCode content:', savedRobotFrameworkCode);
    if (savedRobotFrameworkCode && !isPrintingRobot) {
      console.log('✅ Displaying detailed variant analysis');
      setShowRobotFramework(true);
      setRobotFrameworkCode(savedRobotFrameworkCode);
    } else {
      console.log('❌ Cannot display - savedRobotFrameworkCode is empty or isPrintingRobot is true');
    }
  };

  const cancel = () => abortRef.current?.abort();

  const clearAll = () => {
    setQuestion("");
    setAnalysis("");
    setTestCasesText("");
    setRobotFrameworkCode("");
    setSavedRobotFrameworkCode("");
    setShowRobotFramework(false);
    setIsPrintingRobot(false);
    setIsGeneratingRobot(false);
    setDynamicTestcases([]);
    setExpandedTestcases({});
    setDynamicRobotTests([]);
    setExpandedRobotTests([]);
    setChartData(null);
    setErr(null);
    currentSectionRef.current = "analysis";
    textBufferRef.current = "";
    chartDataSetRef.current = false;

    // Reset parser
    if (testcaseParser.current) {
      testcaseParser.current.resetState();
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 text-gray-900">
      <div className="mx-auto px-4 py-4 h-screen">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-2rem)]">
          {/* LEFT: typing area (no inner scroll) */}
          <div className="bg-white rounded-2xl shadow-md flex flex-col">
            <div className="px-5 py-3 border-b">
              <h1 className="text-xl font-semibold"><span className="font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded">MBSE-AI</span> Query Intake</h1>
            </div>
            <div className="p-5 flex-1 flex flex-col">

            <div className="space-y-3">
              {/* Crash-Analysis-AI compartment */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-visible">
                <div className="bg-gradient-to-r from-blue-100 to-blue-50 px-4 py-2 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <ChipIcon className="w-5 h-5 text-blue-700" />
                    <span style={{marginTop: '-3px'}}>Door crash variant analysis and detailed queries</span>
                  </h3>
                </div>
                <div className="p-4">
                <div className="flex flex-col space-y-4">
                  <div className="relative group">
                    <button
                      onClick={generateTestcases}
                      disabled={loading}
                      className="flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-800 transition text-center w-full"
                    >
                      Door Crash: Variant Analysis (high thresholds)
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[60]">
                      <div className="text-center">
                        <div>Analyzes door crash variants based on force and displacement criteria.</div>
                        <div>Identifies optimal variants, calculates costs, and generates force curves.</div>
                        <div>Shows qualifying variants with detailed properties and visualizations.</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                  <div className="relative group">
                    <button
                      onClick={generateReviews}
                      disabled={loading}
                      className="flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-800 transition text-center w-full"
                    >
                      Door Crash: Variant Analysis (lower thresholds)
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[60]">
                      <div className="text-center">
                        <div>Alternative analysis with <strong>lower force thresholds</strong> (33/42/63 kN)</div>
                        <div>instead of standard criteria (35/45/65 kN).</div>
                        <div>Identifies more variants with slightly relaxed requirements.</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                  <div className="border-t border-gray-300 my-4 -mx-4"></div>
                  <div className="relative group">
                    <button
                      onClick={generateRobotFrameworkTests}
                      disabled={loading || isPrintingRobot || !savedRobotFrameworkCode}
                      className="flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-800 transition text-center w-full"
                    >
                      {isPrintingRobot && (
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                      )}
                      Detailed Variant Analysis
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[60]">
                      <div className="text-center">
                        <div>Displays detailed analysis for each extracted variant and summary of the results.</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
                </div>
              </div>

              <div className="border-t border-gray-200 -mx-5"></div>

              {/* Chat input */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-visible">
                <div className="bg-gradient-to-r from-blue-100 to-blue-50 px-4 py-2 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-700" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                      <text x="12" y="16" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" textAnchor="middle" fill="currentColor">?</text>
                    </svg>
                    <span style={{marginTop: '-3px'}}>Your question</span>
                  </h3>
                </div>
                <div className="p-0">
                <textarea
                  ref={textareaRef}
                  id="question"
                  value={question}
                  onChange={handleQuestionChange}
                  placeholder="Type your question here..."
                  rows="2"
                  className="w-full resize-y rounded-b-xl border-0 p-3 focus:outline-none focus:ring-0 block"
                  style={{
                    minHeight: TEXTAREA_CONFIG.MIN_HEIGHT,
                    maxHeight: `${TEXTAREA_CONFIG.MAX_HEIGHT}px`,
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgb(156 163 175) transparent',
                    display: 'block',
                    marginBottom: '0'
                  }}
                />
                </div>
              </div>

              <div className="border-t border-gray-200 -mx-5"></div>

              {/* Send, Clear and Cancel buttons row */}
              <div className="flex gap-2 justify-end">
                <button
                  onClick={ask}
                  disabled={loading || question.trim().length === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-800 transition"
                >
                  {loading && (
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  )}
                  Send
                </button>
                <button
                  onClick={clearAll}
                  disabled={question.trim().length === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700 transition"
                >
                  Clear
                </button>
                {loading && (
                  <button
                    onClick={cancel}
                    className="inline-flex items-center gap-2 rounded-lg bg-gray-600 px-4 py-2 text-white font-medium hover:bg-gray-700 transition"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {err && <p className="mt-3 text-sm text-red-600">{err}</p>}

            <div className="mt-auto pt-4 flex justify-end">
              <img
                src={logo2}
                alt="Company logo 2"
                className="h-8 w-auto opacity-80 select-none pointer-events-none"
              />
            </div>
            </div>
          </div>

          {/* RIGHT: Three separate output panels */}
          <div className="bg-white rounded-2xl shadow-md flex flex-col h-full overflow-hidden lg:col-span-2">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold"><span className="font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded">MBSE-AI</span> Generative Output</h2>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 leading-none -translate-y-0.5">Streaming</span>
                <button
                  onClick={() => setIsStreamingEnabled(!isStreamingEnabled)}
                  disabled={loading || isPrintingRobot}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isStreamingEnabled ? 'bg-blue-600' : 'bg-gray-300'
                  } ${(loading || isPrintingRobot) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isStreamingEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex-1 p-5 flex flex-col gap-3 overflow-hidden min-h-0 relative">
              {/* Analysis Panel */}
              <div
                className={`bg-blue-50 rounded-xl flex-1 flex flex-col overflow-hidden min-h-0 ${
                  expandedPanel === 'analysis'
                    ? 'absolute top-5 left-5 right-5 bottom-5 z-10 shadow-2xl transition-all duration-700'
                    : expandedPanel && expandedPanel !== 'analysis'
                    ? 'opacity-30 transition-opacity duration-0'
                    : 'transition-all duration-0'
                }`}
              >
                <div className="relative">
                  <div className="px-4 py-2 bg-blue-100 rounded-t-xl">
                    <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                      📊 Analysis & Reasoning
                      <InfoIcon
                        theme="blue"
                        tooltip="Retruns a detailed view on the reasoning of the AI to answer the questions, based on provided data and query. "
                      />
                    </h3>
                  </div>
                  <ExpandButton panelId="analysis" isExpanded={expandedPanel === 'analysis'} onToggle={handlePanelClick} theme="blue" />
                </div>
                <div className="flex-1 px-4 pb-4 overflow-y-auto min-h-0" ref={scrollRefAnalysis}>
                  <div className="whitespace-pre-wrap text-sm pt-2">{analysis}</div>

                  {/* Debug info */}
                  {loading && !analysis && (
                    <div className="text-gray-500 text-sm pt-2 italic">
                      Waiting for analysis content...
                    </div>
                  )}
                </div>
              </div>

              {/* Test Cases Panel */}
              <div
                className={`bg-green-50 rounded-xl flex-1 flex flex-col overflow-hidden min-h-0 ${
                  expandedPanel === 'testcases'
                    ? 'absolute top-5 left-5 right-5 bottom-5 z-10 shadow-2xl transition-all duration-700'
                    : expandedPanel && expandedPanel !== 'testcases'
                    ? 'opacity-30 transition-opacity duration-0'
                    : 'transition-all duration-0'
                }`}
              >
                <div className="relative">
                  <div className="px-4 py-2 bg-green-100 rounded-t-xl">
                    <h3 className="font-semibold text-green-800 flex items-center gap-2">
                      📈 Force Curves & Analysis
                      <InfoIcon
                        theme="green"
                        tooltip="Displays interactive force curves for qualifying variants and detailed analysis results."
                      />
                    </h3>
                  </div>
                  <ExpandButton panelId="testcases" isExpanded={expandedPanel === 'testcases'} onToggle={handlePanelClick} theme="green" />
                </div>
                <div className="flex-1 px-4 pb-4 flex flex-col min-h-0" ref={scrollRefTestCases}>
                  {/* Debug info */}
                  {loading && !testCasesText && !chartData && (
                    <div className="text-gray-500 text-sm pt-2 italic">
                      Waiting for force curve data...
                    </div>
                  )}

                  {/* Line Chart */}
                  {chartData && (
                    <div className="flex-1 min-h-0 pt-2">
                      <ForceLineChart chartData={chartData} />
                    </div>
                  )}

                  {/* Debug: Show if JSON was detected but chart not showing */}
                  {!chartData && testCasesText && testCasesText.includes('"series"') && !loading && (
                    <div className="pt-2 p-3 bg-yellow-100 border border-yellow-300 rounded text-sm text-yellow-800">
                      ⚠️ JSON detected in text but chart not rendering. Check console for errors.
                    </div>
                  )}

                  {/* Show text without JSON when chart is displayed */}
                  <div className={`overflow-y-auto ${chartData ? 'max-h-40' : 'flex-1'}`}>
                    <div className="whitespace-pre-wrap text-sm pt-2 text-gray-700">
                    {(() => {
                      if (!testCasesText) return '';

                      let displayText = testCasesText;

                      // First: Check for ENDOFJSONOUTPUT or ENDOFTESTCASES marker and remove everything after it
                      const jsonOutputIndex = testCasesText.indexOf('ENDOFJSONOUTPUT');
                      const testcasesMarkerIndex = testCasesText.indexOf('ENDOFTESTCASES');
                      const hasEndMarker = jsonOutputIndex !== -1 || testcasesMarkerIndex !== -1;

                      let cutoffIndex = -1;
                      if (jsonOutputIndex !== -1 && testcasesMarkerIndex !== -1) {
                        // Both found, use the one that comes first
                        cutoffIndex = Math.min(jsonOutputIndex, testcasesMarkerIndex);
                      } else if (jsonOutputIndex !== -1) {
                        cutoffIndex = jsonOutputIndex;
                      } else if (testcasesMarkerIndex !== -1) {
                        cutoffIndex = testcasesMarkerIndex;
                      }

                      if (cutoffIndex !== -1) {
                        displayText = displayText.substring(0, cutoffIndex).trim();
                      }

                      // Second: Remove the JSON from the displayed text if we have chart data OR if marker is present
                      const shouldRemoveJson = chartData || hasEndMarker;

                      if (shouldRemoveJson) {
                        const seriesIndex = displayText.indexOf('"series"');
                        if (seriesIndex !== -1) {
                          // Find opening brace before "series"
                          let openBraceIndex = -1;
                          for (let i = seriesIndex; i >= 0; i--) {
                            if (displayText[i] === '{') {
                              openBraceIndex = i;
                              break;
                            }
                          }

                          if (openBraceIndex !== -1) {
                            // Find matching closing brace
                            let braceCount = 0;
                            let closeBraceIndex = -1;
                            for (let i = openBraceIndex; i < displayText.length; i++) {
                              if (displayText[i] === '{') braceCount++;
                              if (displayText[i] === '}') {
                                braceCount--;
                                if (braceCount === 0) {
                                  closeBraceIndex = i;
                                  break;
                                }
                              }
                            }

                            if (closeBraceIndex !== -1) {
                              // Remove the JSON part from the text
                              const beforeJson = displayText.substring(0, openBraceIndex).trim();
                              const afterJson = displayText.substring(closeBraceIndex + 1).trim();
                              displayText = (beforeJson + '\n\n' + afterJson).trim();
                            }
                          }
                        }
                      }

                      return displayText;
                    })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Variant Analysis Panel */}
              <div
                className={`bg-orange-100 rounded-xl flex-1 flex flex-col overflow-hidden min-h-0 ${
                  expandedPanel === 'robot'
                    ? 'absolute top-5 left-5 right-5 bottom-5 z-10 shadow-2xl transition-all duration-700'
                    : expandedPanel && expandedPanel !== 'robot'
                    ? 'opacity-30 transition-opacity duration-0'
                    : 'transition-all duration-0'
                }`}
              >
                <div className="relative">
                  <div className="px-4 py-2 bg-orange-200 rounded-t-xl">
                    <h3 className="font-semibold text-orange-900 flex items-center gap-2">
                      📃 Detailed Variant Analysis
                      <InfoIcon
                        theme="orange"
                        tooltip="Displays detailed analysis for each extracted variant and summary of the results"
                      />
                    </h3>
                  </div>
                  <ExpandButton panelId="robot" isExpanded={expandedPanel === 'robot'} onToggle={handlePanelClick} theme="orange" />
                </div>
                <div className="flex-1 px-4 pb-4 overflow-y-auto min-h-0" ref={scrollRefCode}>
                  {/* Display detailed variant analysis text */}
                  {showRobotFramework && robotFrameworkCode ? (
                    <pre className="text-black text-xs overflow-auto whitespace-pre-wrap pt-2">
                      {robotFrameworkCode}
                    </pre>
                  ) : (
                    <div className="text-gray-500 text-sm pt-2 italic">
                      {savedRobotFrameworkCode && !loading && !showRobotFramework
                        ? "Detailed variant analysis can be displayed."
                        : isGeneratingRobot
                        ? "Detailed variant analysis is generating!"
                        : "No detailed variant analysis available."}
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}